import { Button, LoadingOverlay, Select } from "@mantine/core"
import React, { useEffect, useState } from "react"
import { useLazyGetLeasesQuery } from "../../lease/store/lease.query"
import { useTranslation } from "react-i18next"
import { motion } from "framer-motion"
import { Lease, LeaseStatus } from "../../../models/lease.model"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import { Pagination } from "../../../shared/component/pagination/pagination"
import EmptyIcon from "../../../shared/component/empty-icon/empty-icon"
import { CollectionQuery, Filter } from "../../../models/collection.model"
import dayjs from "dayjs"
import { useNavigate } from "react-router-dom"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
const ExpiringLeases = () => {
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  const { t } = useTranslation()
  const [getLeases, leases] = useLazyGetLeasesQuery()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<string>("1week")

  function parseFilters() {
    let res: Filter[][] = []
    if (filter === "1week") {
      res.push([
        {
          field: "paymentDueDate",
          operator: "<=",
          value: dayjs().add(7, "day").toISOString(),
        },
      ], [{
        field: 'leaseStatus', operator: '=', value: LeaseStatus.Active
      }])
      res.push([
        {
          field: "paymentDueDate",
          operator: ">",
          value: dayjs().toISOString(),
        },
      ])
      if (userInfo?.role?.key == "owner") {
        res.push([
          { field: "property.ownerId", operator: "=", value: ownerInfo?.parentOwnerId },
        ])
      }
    }

    if (filter === "2week") {
      res.push([
        {
          field: "paymentDueDate",
          operator: "<=",
          value: dayjs().add(2, "week").toISOString(),
        },
      ],[{
        field: 'leaseStatus', operator: '=', value: LeaseStatus.Active
      }])
      res.push([
        {
          field: "paymentDueDate",
          operator: ">",
          value: dayjs().toISOString(),
        },
      ])
      if (userInfo?.role?.key == "owner") {
        res.push([
          { field: "property.ownerId", operator: "=", value: ownerInfo?.parentOwnerId },
        ])
      }
    }
    if (filter === "1month") {
      res.push([
        {
          field: "paymentDueDate",
          operator: "<=",
          value: dayjs().add(1, "month").toISOString(),
        },
      ],[{
        field: 'leaseStatus', operator: '=', value: LeaseStatus.Active
      }])

      res.push([
        {
          field: "paymentDueDate",
          operator: ">",
          value: dayjs().toISOString(),
        },
      ])
      if (userInfo?.role?.key == "owner") {
        res.push([
          { field: "property.ownerId", operator: "=", value: ownerInfo?.parentOwnerId },
        ])
      }
    }
    if (filter === "expired") {
      res.push([
        {
          field: "paymentDueDate",
          operator: "<",
          value: dayjs().toISOString(),
        },
      ])

      if (userInfo?.role?.key == "owner") {
        res.push([
          { field: "property.ownerId", operator: "=", value: ownerInfo?.parentOwnerId },
        ])
      }
    }

    res.push([
      { field: "leaseStatus", operator: "!=", value: LeaseStatus.Pending },
    ])

    return res
  }

  const [collection, setCollection] = useState<CollectionQuery>({
    top: 10,
    skip: 0,
    orderBy: [{ field: "createdAt", direction: "desc" }],
    includes: ["tenant", "property", "property.owner"],
    filter: parseFilters(),
  })

  useEffect(() => {
    getLeases({ ...collection, filter: parseFilters() })
  }, [filter, collection])
  return (
    <div className="bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-lg shadow-lg rounded-xl p-6 border border-gray-700 mb-8">
      <LoadingOverlay visible={leases?.isLoading || leases?.isFetching} />

      <div className="flex justify-between">
        <h2>{t("upcomingPayments")}</h2>

        <Select
          className=""
          value={filter}
          onChange={(e) => setFilter(e as string)}
          data={[
            { label: t("expiringOneWeek"), value: "1week" },
            { label: t("expiringTwoWeek"), value: "2week" },
            { label: t("expiringOneMonth"), value: "1month" },
            { label: t("expired"), value: "expired" },
          ]}
        />
      </div>

      <div className="px-2 overflow-x-auto relative">
        <table className="min-w-full divide-y divide-gray-700">
          <thead
          // className="text-xs text-gray-700 capitalize bg-gray-50 dark:bg-gray-700 dark:text-gray-400 "
          >
            <tr className="text-nowrap">
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("lease", { count: 1 })}
              </th>

              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("tenant", { count: 1 })}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("paymentDueDate")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("rentAmount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {leases?.data?.data &&
              leases?.data?.data.length > 0 &&
              leases?.data?.data.map((lease: Lease, idx: number) => (
                <motion.tr
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white border-b dark:bg-gray-800 text-xs dark:border-gray-700 cursor-pointer"
                  onClick={() => {
                    navigate(`/lease/detail/active/${lease.id as string}`)
                  }}
                >
                  <th className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {lease?.name}
                  </th>
                  {/* <td className="py-4 px-6 text-xs">{document?.referenceNumber}</td> */}

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {lease?.tenant?.name}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {lease?.paymentDueDate ? (
                      <span>
                        {dateFormat(lease?.paymentDueDate, {
                          weekday: true,
                          day: true,
                          month: true,
                          year: true,
                          hour: false,
                        })}
                      </span>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {lease?.rentAmount}
                  </td>
                </motion.tr>
              ))}
          </tbody>
        </table>
        {!leases?.data?.data?.length ? (
          <div className="w-full relative flex justify-center items-center h-56">
            <EmptyIcon />
          </div>
        ) : (
          <></>
        )}

        {leases?.data && leases?.data?.count > 0 && (
          <div className="w-full flex justify-end items-center mt-2 mb-2">
            <Pagination
              total={leases?.data?.count as number}
              defaultPageSize={10}
              pageSize={[5, 10, 15, 20]}
              onPaginationChange={(skip: number, top: number) => {
                setCollection({
                  ...collection,
                  skip: skip,
                  top: top,
                })
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default ExpiringLeases
